import {
  ForwardedRef,
  forwardRef,
  MouseEvent,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useSyncExternalStore,
} from 'react'
import { createPortal } from 'react-dom'

import { MODAL_ROOT_ID } from 'components/globals'
import styles from 'styles/components/Modal.module.css'

export type ModalHandles = {
  focusClose: () => void
  closeModal: (e?: KeyboardEvent | MouseEvent) => void
}
export type ModalContainerProps = {
  onClose: () => void
  children: ReactNode
  label: { id: string } | { value: string }
  fermable?: boolean
}

// TODO handle click outside modal
function ModalContainer(
  { children, onClose, label, fermable = true }: ModalContainerProps,
  ref: ForwardedRef<ModalHandles>
) {
  const modalContainerRef = useRef<HTMLDivElement>(null)
  const isRendered = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  )
  const previousFocusedElement = useRef<HTMLElement | null>(null)

  function focusClose() {
    if (!previousFocusedElement.current)
      previousFocusedElement.current = document.activeElement as HTMLElement

    const premierElementFocusable =
      modalContainerRef.current!.querySelector<HTMLElement>(
        'button:first-child'
      ) ??
      modalContainerRef.current!.querySelector<HTMLElement>(
        '[href], button, textarea, input, select'
      )
    premierElementFocusable?.focus()
  }

  function handleTabKey(e: KeyboardEvent) {
    if (!modalContainerRef.current) return

    const focusableModalElements: NodeListOf<HTMLElement> =
      modalContainerRef.current.querySelectorAll(
        '[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
    const firstTabElement = focusableModalElements[0]
    const lastTabElement =
      focusableModalElements[focusableModalElements.length - 1]

    if (!e.shiftKey && document.activeElement === lastTabElement) {
      firstTabElement.focus()
      e.preventDefault()
    }
    if (e.shiftKey && document.activeElement === firstTabElement) {
      lastTabElement.focus()
      e.preventDefault()
    }
  }

  function handleClose(e?: KeyboardEvent | MouseEvent) {
    e?.preventDefault()
    if (previousFocusedElement.current) previousFocusedElement.current.focus()
    onClose()
  }

  function keyListener(e: KeyboardEvent) {
    const listener = keyListeners.current[e.key]
    return listener && listener(e)
  }

  const keyListeners = useRef<{
    [key: string]: (e: KeyboardEvent) => void
  }>(
    fermable
      ? { Tab: handleTabKey, Escape: handleClose }
      : { Tab: handleTabKey }
  )

  useImperativeHandle(ref, () => ({
    focusClose,
    closeModal: handleClose,
  }))

  useEffect(() => {
    document.addEventListener('keydown', keyListener)
    focusClose()

    return () => document.removeEventListener('keydown', keyListener)
  }, [])

  const modalContainer = (
    <div
      ref={modalContainerRef}
      role='dialog'
      aria-modal={true}
      aria-labelledby={isExternal(label) ? label.id : undefined}
      aria-label={!isExternal(label) ? label.value : undefined}
      className={styles.modalOverlay}
    >
      {children}
    </div>
  )

  if (isRendered) {
    const modalRoot = document.getElementById(MODAL_ROOT_ID)
    return modalRoot ? createPortal(modalContainer, modalRoot) : null
  } else {
    return null
  }
}

function isExternal(title: { id: string } | object): title is { id: string } {
  return Object.prototype.hasOwnProperty.call(title, 'id')
}

export default forwardRef(ModalContainer)
